import { useState, useMemo } from "react";
import { useData } from "@/context/data-provider";
import type { Visit, Doctor } from "@/types";
import { DoctorDetailModal } from "@/components/shared/doctor-detail-modal";

function getWeekLabel(dateStr: string) {
  const d = new Date(dateStr);
  const start = new Date(d);
  start.setDate(d.getDate() - d.getDay() + 1);
  return `${start.getDate()}/${start.getMonth() + 1}`;
}

function BarChart({ data }: { data: { label: string; value: number; color: string }[] }) {
  const max = Math.max(1, ...data.map((d) => d.value));
  return (
    <div className="bar-chart">
      {data.map((d) => (
        <div key={d.label} className="bar-col">
          <div className="bar-val">{d.value}</div>
          <div className="bar" style={{ height: `${(d.value / max) * 100}%`, background: d.color }} />
          <div className="bar-label">{d.label}</div>
        </div>
      ))}
    </div>
  );
}

export default function Analise() {
  const {
    doctors: { doctors },
    visits: { visits },
    period: { period },
    apoios: { apoios },
  } = useData();

  const [detailDoc, setDetailDoc] = useState<Doctor | null>(null);

  const periodVisits = useMemo(() => {
    const all: Visit[] = [];
    for (const [date, dayVisits] of Object.entries(visits)) {
      if (date >= period.start && date <= period.end) all.push(...dayVisits);
    }
    return all;
  }, [visits, period]);

  const weeklyData = useMemo(() => {
    const weeks: Record<string, number> = {};
    for (const [date, dayVisits] of Object.entries(visits)) {
      if (date >= period.start && date <= period.end) {
        const week = getWeekLabel(date);
        weeks[week] = (weeks[week] ?? 0) + dayVisits.length;
      }
    }
    return Object.entries(weeks).slice(-8).map(([label, value]) => ({
      label, value, color: "linear-gradient(180deg,#1B4F9B,#2E7BC4)",
    }));
  }, [visits, period]);

  const byPriority = useMemo(() => {
    const counts = { alta: 0, media: 0, baixa: 0 };
    for (const v of periodVisits) {
      const doc = doctors.find((d) => d.id === v.doctorId);
      if (doc) counts[doc.prio]++;
    }
    return [
      { label: "Alta", value: counts.alta, color: "linear-gradient(180deg,#C0392B,#E74C3C)" },
      { label: "Média", value: counts.media, color: "linear-gradient(180deg,#1B4F9B,#2E7BC4)" },
      { label: "Baixa", value: counts.baixa, color: "linear-gradient(180deg,#4A9E2F,#5BAD3E)" },
    ];
  }, [periodVisits, doctors]);

  const topProducts = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const v of periodVisits) for (const p of v.products) counts[p] = (counts[p] ?? 0) + 1;
    return Object.entries(counts).sort((a, b) => b[1] - a[1]).slice(0, 7)
      .map(([label, value]) => ({ label, value, color: "linear-gradient(180deg,#6C3483,#8E44AD)" }));
  }, [periodVisits]);

  const doctorCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const v of periodVisits) if (v.doctorId) counts[v.doctorId] = (counts[v.doctorId] ?? 0) + 1;
    return counts;
  }, [periodVisits]);

  const topDoctors = useMemo(() => {
    return Object.entries(doctorCounts).sort((a, b) => b[1] - a[1]).slice(0, 8)
      .map(([id, count]) => ({ doc: doctors.find((d) => d.id === id), count }))
      .filter((d) => d.doc);
  }, [doctorCounts, doctors]);

  const bottomDoctors = useMemo(() => {
    return doctors.map((d) => ({ doc: d, count: doctorCounts[d.id] ?? 0 })).sort((a, b) => a.count - b.count).slice(0, 8);
  }, [doctors, doctorCounts]);

  return (
    <div className="container">
      <h1 className="font-serif text-[1.3rem] mb-1" style={{ color: "var(--mv-ink-dark)" }}>Análise</h1>
      <p className="font-mono text-[0.68rem] mb-4" style={{ color: "var(--mv-ink-light)" }}>
        {period.start} — {period.end} · {periodVisits.length} visitas
      </p>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-[14px] mb-4">
        <div className="mv-panel">
          <div className="mv-panel-header"><span className="mv-panel-title">Visitas por Semana</span></div>
          <div className="p-[16px_18px]">
            {weeklyData.length === 0 ? <div className="empty-state">Sem dados</div> : <BarChart data={weeklyData} />}
          </div>
        </div>
        <div className="mv-panel">
          <div className="mv-panel-header"><span className="mv-panel-title">Visitas por Prioridade</span></div>
          <div className="p-[16px_18px]"><BarChart data={byPriority} /></div>
        </div>
        {topProducts.length > 0 && (
          <div className="mv-panel">
            <div className="mv-panel-header"><span className="mv-panel-title">Top Produtos</span></div>
            <div className="p-[16px_18px]"><BarChart data={topProducts} /></div>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-[18px]">
        <div className="mv-panel">
          <div className="mv-panel-header"><span className="mv-panel-title">🏆 Mais Visitados</span></div>
          <div className="max-h-[280px] overflow-y-auto">
            {topDoctors.length === 0 ? <div className="empty-state">Sem dados</div> : (
              topDoctors.map((d, i) => (
                <div key={d.doc!.id} className="doctor-item" onClick={() => setDetailDoc(d.doc!)}>
                  <span className="font-mono text-[0.6rem] w-5 text-center" style={{ color: "var(--mv-ink-light)" }}>#{i + 1}</span>
                  <div className={`prio-dot prio-dot-${d.doc!.prio}`} />
                  <div className="flex-1 min-w-0">
                    <span className="font-semibold text-[0.81rem]">{d.doc!.name}</span>
                    <div className="text-[0.67rem]" style={{ color: "var(--mv-ink-light)" }}>{d.doc!.local}</div>
                  </div>
                  <span className="font-mono text-[0.68rem] font-semibold" style={{ color: "var(--mv-green)" }}>{d.count}</span>
                </div>
              ))
            )}
          </div>
        </div>
        <div className="mv-panel">
          <div className="mv-panel-header"><span className="mv-panel-title">⚠️ Menos Visitados</span></div>
          <div className="max-h-[280px] overflow-y-auto">
            {bottomDoctors.length === 0 ? <div className="empty-state">Sem dados</div> : (
              bottomDoctors.map((d, i) => (
                <div key={d.doc.id} className="doctor-item" onClick={() => setDetailDoc(d.doc)}>
                  <span className="font-mono text-[0.6rem] w-5 text-center" style={{ color: "var(--mv-ink-light)" }}>#{i + 1}</span>
                  <div className={`prio-dot prio-dot-${d.doc.prio}`} />
                  <div className="flex-1 min-w-0">
                    <span className="font-semibold text-[0.81rem]">{d.doc.name}</span>
                    <div className="text-[0.67rem]" style={{ color: "var(--mv-ink-light)" }}>{d.doc.local}</div>
                  </div>
                  <span className="font-mono text-[0.68rem] font-semibold" style={{ color: "var(--mv-red)" }}>{d.count}</span>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {detailDoc && (
        <DoctorDetailModal
          doctor={detailDoc}
          visits={visits}
          period={period}
          apoios={apoios}
          onClose={() => setDetailDoc(null)}
          onEdit={() => setDetailDoc(null)}
        />
      )}
    </div>
  );
}
